'use client';

import { useQuery } from '@zg-rentals/react-query5';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useFeatureFlag } from '@/src/lib/feature-flags/useFeatureFlag';
import {
  APARTMENT_REVIEWS_STALE_TIME_MS,
  fetchBuildingReviews,
  isApartmentReviewsEligible,
  type BuildingReviewsSnapshot,
} from '@/src/lib/api/endpoints/apartmentReviews';

export const apartmentReviewsKeys = {
  all: ['apartmentReviews'] as const,
  building: (lotId: string) => ['apartmentReviews', 'building', lotId] as const,
};

export function useApartmentReviews(
  listing: PropertyDetailListing,
  pdpType: 'unit' | 'building',
  snapshot?: BuildingReviewsSnapshot,
) {
  const enabled = useFeatureFlag('apartment-reviews') && isApartmentReviewsEligible(listing);
  const lotId = listing.maloneLotIdEncoded ?? '';
  const seed = snapshot?.lotId === lotId ? snapshot : undefined;
  const query = useQuery({
    queryKey: apartmentReviewsKeys.building(lotId),
    queryFn: ({ signal }) => fetchBuildingReviews(lotId, signal),
    enabled: enabled && pdpType === 'building' && !!lotId,
    initialData: seed?.reviews,
    initialDataUpdatedAt: seed?.updatedAt,
    staleTime: APARTMENT_REVIEWS_STALE_TIME_MS,
    retry: false,
    meta: { tags: ['apartmentReviews'], context: { api: 'hotpads-api', details: 'building reviews' } },
  });
  return enabled ? (pdpType === 'unit' ? listing.reviews ?? null : query.data ?? null) : null;
}
